// Client-side DISPLAY PREFERENCES only (docs/09 §3/§4): the theme choice and
// the last-active working scope ids. These are preferences, not credentials —
// no token or sensitive data ever lands in browser storage; the session stays
// an HttpOnly cookie. scripts/check_static_invariants.py allowlists exactly
// this module's named storage keys.

// The theme preference. "system" follows `prefers-color-scheme` live; the
// stored key holds only explicit "light"/"dark" choices (absent = system),
// which keeps migration from the pre-system two-state model trivial.
export type ThemePreference = "dark" | "light" | "system";
export type EffectiveTheme = "dark" | "light";

const THEME_STORAGE_KEY = "marker.theme";

// Last-active working scope (docs/09 §4): the front door reopens on this
// `(team, agent)` pair; ids that no longer resolve are dropped by the shell's
// agent 404 fallback, not here.
const TEAM_SCOPE_STORAGE_KEY = "marker.scope.team";
const AGENT_SCOPE_STORAGE_KEY = "marker.scope.agent";

// The sidebar collapsed/expanded choice (docs/09 §4): a display preference like
// the theme. Absent = unset, which defers to the shell's responsive media-query
// default; an explicit toggle persists the boolean across reloads.
const SIDEBAR_COLLAPSED_STORAGE_KEY = "marker.sidebar.collapsed";
const SIDEBAR_COLLAPSED_MEDIA_QUERY = "(max-width: 47.5rem)";

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const APP_FAVICON_LINK_ID = "marker-app-favicon";
const APP_FAVICON_HREFS: Record<EffectiveTheme, string> = {
  dark: "/brand/marker-app-favicon-dark.svg",
  light: "/brand/marker-app-favicon-light.svg",
};

export function assertPreferenceIsNotSensitive(isSensitive: boolean): void {
  if (isSensitive) {
    throw new Error("Sensitive values cannot be persisted as preferences");
  }
}

function isEffectiveTheme(value: string | null): value is EffectiveTheme {
  return value === "dark" || value === "light";
}

export function persistThemePreference(preference: ThemePreference): void {
  if (typeof window === "undefined") {
    return;
  }

  if (preference === "system") {
    window.localStorage.removeItem(THEME_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(THEME_STORAGE_KEY, preference);
}

// Stored explicit "light"/"dark" values (including pre-system-mode ones) keep
// working; anything else means "system".
export function readThemePreference(): ThemePreference {
  if (typeof window === "undefined") {
    return "system";
  }

  const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY);
  return isEffectiveTheme(storedTheme) ? storedTheme : "system";
}

export function systemPrefersDark(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

export function resolveEffectiveTheme(
  preference: ThemePreference,
  prefersDark: boolean,
): EffectiveTheme {
  if (preference === "system") {
    return prefersDark ? "dark" : "light";
  }
  return preference;
}

// Applies the EFFECTIVE theme: the root theme class plus the theme-matched
// app favicon.
export function applyTheme(theme: EffectiveTheme): void {
  if (typeof document === "undefined") {
    return;
  }

  document.documentElement.classList.toggle(
    "marker-theme-dark",
    theme === "dark",
  );
  document.documentElement.classList.toggle(
    "marker-theme-light",
    theme === "light",
  );

  document
    .getElementById(APP_FAVICON_LINK_ID)
    ?.setAttribute("href", APP_FAVICON_HREFS[theme]);
}

// The initial sidebar state: a prior explicit toggle wins across reloads;
// unset defers to the responsive media-query default so first-visit behavior
// is unchanged (docs/09 §4).
export function readSidebarCollapsed(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const stored = window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY);
  if (stored === "collapsed") {
    return true;
  }
  if (stored === "expanded") {
    return false;
  }
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia(SIDEBAR_COLLAPSED_MEDIA_QUERY).matches
  );
}

export function persistSidebarCollapsed(collapsed: boolean): void {
  if (typeof window === "undefined") {
    return;
  }

  const value = collapsed ? "collapsed" : "expanded";
  window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, value);
}

export interface LastActiveScope {
  agent?: string;
  team?: string;
}

// Shape-validated read (uuid pattern only — existence is validated by the
// shell's resolution chain and agent 404 fallback, docs/09 §4).
export function readLastActiveScope(): LastActiveScope {
  if (typeof window === "undefined") {
    return {};
  }

  const scope: LastActiveScope = {};
  const team = window.localStorage.getItem(TEAM_SCOPE_STORAGE_KEY);
  if (team !== null && UUID_PATTERN.test(team)) {
    scope.team = team;
  }
  const agent = window.localStorage.getItem(AGENT_SCOPE_STORAGE_KEY);
  if (agent !== null && UUID_PATTERN.test(agent)) {
    scope.agent = agent;
  }
  return scope;
}

export function persistLastActiveScope(scope: LastActiveScope): void {
  if (typeof window === "undefined") {
    return;
  }

  if (scope.team === undefined) {
    window.localStorage.removeItem(TEAM_SCOPE_STORAGE_KEY);
  } else {
    window.localStorage.setItem(TEAM_SCOPE_STORAGE_KEY, scope.team);
  }
  if (scope.agent === undefined) {
    window.localStorage.removeItem(AGENT_SCOPE_STORAGE_KEY);
  } else {
    window.localStorage.setItem(AGENT_SCOPE_STORAGE_KEY, scope.agent);
  }
}

// Law: docs/09a-frontend-screens.md (Transcript results rail).
// ASSUMED(the disclosure key follows marker.* naming with the viewer id).
function measuredOpenKey(viewerId: string): string {
  return `marker.results.measured.${viewerId}`;
}

export function readMeasuredOpen(viewerId?: string): boolean {
  if (typeof window === "undefined" || viewerId == null) {
    return false;
  }
  const MEASURED_OPEN_STORAGE_KEY = measuredOpenKey(viewerId);
  return window.localStorage.getItem(MEASURED_OPEN_STORAGE_KEY) === "open";
}

export function persistMeasuredOpen(
  viewerId: string | undefined,
  open: boolean,
): void {
  if (typeof window === "undefined" || viewerId == null) {
    return;
  }
  const MEASURED_OPEN_STORAGE_KEY = measuredOpenKey(viewerId);
  const value = open ? "open" : "closed";
  window.localStorage.setItem(MEASURED_OPEN_STORAGE_KEY, value);
}
